import { resolvePredefinedVisualizationTarget } from '@/features/visualizations/domain/predefinedDashboardTargets';
import PlatformSearchRepository from '@/pages/home/repositories/PlatformSearchRepository';

const API_RESULT_TYPES = [
  'custom_dashboard',
  'custom_kpi',
  'visualization',
  'dataset',
  'tool',
  'tool_run',
  'active_alert',
  'shadow_trading_asset',
  'news_article',
];

const TOOL_ROUTE_BY_ID = {
  'control-state': '/tools/control-state',
  'bidding-strategy': '/tools/bidding-strategy',
  'forward-benchmark': '/tools/forward-benchmark',
  'imbalance-benchmark': '/tools/imbalance-benchmark',
  'day-ahead-predictor': '/tools/day-ahead-forecast',
  'power-market-simulator': '/tools/power-market-simulator',
  'shadow-trading': '/tools/shadow-trading',
  orderbook: '/tools/orderbook',
};

export function platformSearchTypes() {
  return API_RESULT_TYPES;
}

export async function searchPlatform({ q, types = API_RESULT_TYPES, limit = 8, perTypeLimit = 2 }) {
  const query = String(q || '').trim();
  if (!query) {
    return {
      query: '',
      results: [],
      providerErrors: [],
      total: 0,
    };
  }

  const response = await PlatformSearchRepository.search({
    q: query,
    types: types.join(','),
    limit,
    per_type_limit: perTypeLimit,
  });

  const payload = response?.data || {};
  return {
    query: payload.query || query,
    total: Number(payload.total || 0),
    results: Array.isArray(payload.results) ? payload.results.map(normalizeSearchResult) : [],
    providerErrors: Array.isArray(payload.provider_errors) ? payload.provider_errors : [],
  };
}

export function normalizeSearchResult(result) {
  const metadata = result?.metadata || {};
  const type = result?.type || 'dataset';
  const id = String(result?.id || metadata.asset_id || metadata.dataset_id || '');

  return {
    type,
    id,
    title: result?.title || id,
    description: result?.description || '',
    subtitle: result?.subtitle || '',
    badges: Array.isArray(result?.badges) ? result.badges.filter(Boolean) : [],
    score: Number(result?.score || 0),
    updatedAt: result?.updated_at || null,
    metadata,
    href: resolveSearchHref({ ...result, id, type, metadata }),
    external: type === 'news_article' && Boolean(result?.url && /^https?:\/\//i.test(result.url)),
  };
}

export function resolveSearchHref(result) {
  const metadata = result?.metadata || {};
  const id = String(result?.id || '');

  if (result.type === 'custom_dashboard') {
    return `/dashboards/${encodeURIComponent(metadata.asset_id || id)}`;
  }

  if (result.type === 'custom_kpi') {
    return {
      path: '/my-environment/dashboards',
      query: {
        q: result.title || metadata.asset_id || id,
        asset: metadata.asset_id || id,
        type: result.type,
      },
    };
  }

  if (result.type === 'visualization') {
    const datasetId = metadata.dataset_id || id.split(':')[0];
    const visualizationId = metadata.visualization_id || id.split(':')[1];
    if (datasetId && visualizationId) {
      const dashboardTarget = resolvePredefinedVisualizationTarget(datasetId, visualizationId);
      if (dashboardTarget) {
        return {
          path: dashboardTarget.path,
          query: {
            highlightPanel: dashboardTarget.panelId,
          },
        };
      }

      return `/visualization/${encodeURIComponent(
        visualizationId
      )}/fullscreen?datasetId=${encodeURIComponent(datasetId)}`;
    }
  }

  if (result.type === 'dataset') {
    const datasetId = metadata.dataset_id || id;
    return `/dataset/${encodeURIComponent(datasetId)}`;
  }

  if (result.type === 'tool') {
    return TOOL_ROUTE_BY_ID[id] || normalizeApiPath(result.url) || '/tools';
  }

  if (result.type === 'tool_run') {
    const toolId = metadata.tool_id || id.split(':')[0];
    const runId = metadata.run_id || id.split(':')[1];
    return {
      path: TOOL_ROUTE_BY_ID[toolId] || '/tools',
      query: runId ? { runId } : {},
    };
  }

  if (result.type === 'active_alert') {
    const alertId = metadata.alert_id || id;
    return alertId ? { name: 'alerts.edit', params: { id: alertId } } : '/alerts';
  }

  if (result.type === 'shadow_trading_asset') {
    const assetId = metadata.asset_id || id;
    // A search hit targets one specific simulation — open its dashboard, not the
    // index (which lists all and ignores assetId). The live dashboard renders
    // any asset by id; the index lives at /tools/shadow-trading.
    return assetId
      ? { name: 'shadow-trading-live', query: { assetId } }
      : { path: '/tools/shadow-trading' };
  }

  if (result.type === 'news_article') {
    return result.url || '/news';
  }

  return normalizeApiPath(result.url) || '/';
}

function normalizeApiPath(url) {
  if (!url || typeof url !== 'string') return null;
  return url.replace(/^\/api\/v2/, '') || '/';
}
